"use client";
import Button from "@/components/atoms/Button/Button";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import styles from "./PaymentModule.module.css";
import dayjs from "dayjs";
import LoadingModule from "../LoadingModule/LoadingModule.component";
import ErrorModule from "../ErrorModule/ErrorModule.component";

type onStartCheckout = () => void;
type onCheckoutError = (error: string) => void;
type redirect_url = string;

const STRIPE_PUBLIC_KEY = process.env.NEXT_PUBLIC_STRIPE_KEY as string;

export type PaymentModuleState =
  | "CREATING_ACCOUNT"
  | "ACCEPTING_DETAILS"
  | "CHECKING_OUT"
  | "ERROR";
interface PaymentModuleProps {
  state: PaymentModuleState;
  session_info: SessionInfo;
  stripe_client_secret?: string;
  checkout_error?: string;
  onStartCheckout: onStartCheckout;
  onCheckoutError: onCheckoutError;
  dismissCheckoutError: () => void;
  redirect_url: redirect_url;
}

interface SessionInfo {
  full_name: string;
  start_datetime: string;
  duration: number;
  cost: number;
}

const CheckoutForm = ({
  is_loading,
  session_info,
  onStartCheckout,
  onCheckoutError,
}: {
  is_loading: boolean;
  session_info: SessionInfo;
  onStartCheckout: onStartCheckout;
  onCheckoutError: onCheckoutError;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const concatHostnameAndPathname = () => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    // const port = window.location.port;

    // Ensure a leading slash in the pathname for proper concatenation
    const formattedPathname = pathname.startsWith("/")
      ? pathname
      : "/" + pathname;

    return `https://${hostname}${formattedPathname}`;
  };

  const checkout = async () => {
    if (!stripe || !elements || is_loading) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    onStartCheckout();

    const redirect_url = concatHostnameAndPathname();

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: redirect_url,
      },
    });

    if (error) {
      if ("type" in error && error.type === "card_error") {
        return onCheckoutError(
          error.message || "An unexpected error occurred with your card."
        );
      }
      if ("type" in error && error.type === "validation_error") {
        return onCheckoutError(
          error.message || "An error occurred validating your card."
        );
      }
      return onCheckoutError("An unexpected error occurred.");
    }
  };

  const formatStartDate = (datetime: string) =>
    dayjs(datetime).format("ddd MMM D, h:mma z");

  const { full_name, start_datetime, duration } = session_info;

  return (
    <div>
      <div className={styles.info_container}>
        <h3>Complete Booking</h3>
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00016 14.6667C4.31826 14.6667 1.3335 11.6819 1.3335 8.00004C1.3335 4.31814 4.31826 1.33337 8.00016 1.33337C11.682 1.33337 14.6668 4.31814 14.6668 8.00004C14.6668 11.6819 11.682 14.6667 8.00016 14.6667ZM8.00016 13.3334C10.9457 13.3334 13.3335 10.9456 13.3335 8.00004C13.3335 5.05452 10.9457 2.66671 8.00016 2.66671C5.05464 2.66671 2.66683 5.05452 2.66683 8.00004C2.66683 10.9456 5.05464 13.3334 8.00016 13.3334ZM8.66683 8.00004H11.3335V9.33337H7.3335V4.66671H8.66683V8.00004Z"
              fill="#BFBFBF"
            />
          </svg>
          <p>
            {duration} mins with {full_name}
          </p>
        </div>
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M6.00016 0.666626V1.99996H10.0002V0.666626H11.3335V1.99996H14.0002C14.3684 1.99996 14.6668 2.29844 14.6668 2.66663V13.3333C14.6668 13.7015 14.3684 14 14.0002 14H2.00016C1.63198 14 1.3335 13.7015 1.3335 13.3333V2.66663C1.3335 2.29844 1.63198 1.99996 2.00016 1.99996H4.66683V0.666626H6.00016ZM13.3335 7.33329H2.66683V12.6666H13.3335V7.33329ZM4.66683 3.33329H2.66683V5.99996H13.3335V3.33329H11.3335V4.66663H10.0002V3.33329H6.00016V4.66663H4.66683V3.33329Z"
              fill="#BFBFBF"
            />
          </svg>
          <p>{formatStartDate(start_datetime)}</p>{" "}
        </div>
      </div>
      <div className={styles.payment_element}>
        <PaymentElement id="payment-element" />
        <Button className={styles.checkout_btn} onClick={checkout}>
          {/* TO DO - update session price */}
          {is_loading ? "Loading..." : `Confirm and pay $${session_info.cost}`}
        </Button>
      </div>
    </div>
  );
};

const PaymentModule = ({
  state,
  session_info,
  stripe_client_secret,
  checkout_error,
  onCheckoutError,
  dismissCheckoutError,
  onStartCheckout,
}: PaymentModuleProps) => {
  const [stripe, setStripe] = useState<null | Stripe>(null);

  useEffect(() => {
    loadStripe(STRIPE_PUBLIC_KEY).then((stripe) => setStripe(stripe));
  }, []);

  switch (true) {
    case state === "CREATING_ACCOUNT":
      return <LoadingModule messages={["Creating your account"]} />;
    case state == "ACCEPTING_DETAILS" || state == "CHECKING_OUT":
      const STRIPE_OPTIONS = {
        clientSecret: stripe_client_secret,
      };
      return (
        <div className={styles.container}>
          <Elements stripe={stripe} options={STRIPE_OPTIONS}>
            <CheckoutForm
              is_loading={state == "CHECKING_OUT"}
              session_info={session_info}
              onStartCheckout={onStartCheckout}
              onCheckoutError={onCheckoutError}
            />
          </Elements>
        </div>
      );
    case state === "ERROR":
      return (
        <ErrorModule
          title="An error occured"
          description={checkout_error || "An unexpected error occured."}
          dismiss={dismissCheckoutError}
          contactSupport={() => {
            // open intercom
            console.log("open intercom");
          }}
        />
      );
    default:
      return <LoadingModule messages={["Loading checkout"]} />;
  }
};

export default PaymentModule;
